import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { Spinner } from "@/components/Spinner";
import PostCard from "./PostCard";
import ReportDialog from "./ReportDialog";
import DiscoverStrip from "@/components/DiscoverStrip";
import { toast } from "sonner";
import { Search, X } from "lucide-react";

export default function Feed() {
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  usePageMeta(t("feed"));
  const [tab, setTab] = useState("global");
  const [posts, setPosts] = useState(null);
  const [saved, setSaved] = useState({});
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [reportOn, setReportOn] = useState(null);

  const TABS = [["global", t("feed_tab_global")], ["regional", t("feed_tab_regional")], ["interest", t("feed_tab_interest")]];

  const load = useCallback(async () => {
    setPosts(null);
    try {
      const params = { feed_type: tab, mix: tab === "global" && !search ? 1 : undefined };
      if (search) params.q = search;
      const region = user?.profile?.region;
      const interest = user?.profile?.interests?.[0];
      if (tab === "regional" && region) params.region = region;
      if (tab === "interest" && interest) params.interest = interest;
      const { data } = await api.get("/feed", { params });
      setPosts(Array.isArray(data) ? data : []);
      try { const { data: ids } = await api.get("/bookmarks/ids"); const m = {}; (ids || []).forEach((r) => { if (r?.post_id) m[r.post_id] = true; }); setSaved(m); } catch { /* noop */ }
    } catch { setPosts([]); }
  }, [tab, search, user]);
  useEffect(() => { load(); }, [load]);

  const unlock = async (post) => {
    try { await api.post(`/feed/unlock/${post.id}`); await refreshUser?.(); await load(); }
    catch (e) {
      const msg = String(e?.response?.data?.detail || e?.message || "").toLowerCase();
      if (e?.response?.status === 402 || msg.includes("credit")) {
        if (window.confirm(t("feed_credits_needed", { n: post.price_credits ?? 0 }))) navigate("/app/credits");
      } else { toast.error(apiError(e)); }
    }
  };

  const list = useMemo(() => posts || [], [posts]);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="feed-screen">
      <PageHeader eyebrow={t("nav.group.start")} title={t("feed")} />

      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`feed-tab-${k}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>{label}</button>
        ))}
      </div>

      <DiscoverStrip />

      <form onSubmit={(e) => { e.preventDefault(); setSearch(query.trim()); }} className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("feed_search_placeholder")} data-testid="feed-search-input" className="w-full bg-transparent text-sm outline-none" />
        {query ? <button type="button" onClick={() => { setQuery(""); setSearch(""); }} data-testid="feed-search-clear"><X className="h-4 w-4 text-muted-foreground" /></button> : null}
      </form>

      {posts === null ? <Spinner label={t("common.loading")} /> : list.length === 0 ? (
        <p className="py-12 text-center text-sm italic text-muted-foreground">{t("no_posts_yet")}</p>
      ) : (
        <div className="space-y-4">
          {list.map((p) => <PostCard key={p.id} post={p} savedInit={saved[p.id]} onReport={setReportOn} onUnlock={unlock} onOpen={(post) => navigate(`/app/feed/post/${post.id}`)} />)}
        </div>
      )}

      <ReportDialog post={reportOn} open={!!reportOn} onClose={() => setReportOn(null)} />
    </div>
  );
}
